import CircuitBreaker from "opossum";
import { CircuitOpenError, isCircuitOpenLikeError } from "./errors";
import { emitCircuitOpenAlert } from "./notifier";

export type CircuitState = "closed" | "open" | "half-open";

export interface ManagedCircuitOptions {
  name: string;
  envPrefix?: string;
  timeout?: number;
  defaultTimeout?: number;
  resetTimeout?: number;
  defaultResetTimeout?: number;
  volumeThreshold?: number;
  defaultVolumeThreshold?: number;
  rollingCountTimeout?: number;
  defaultRollingCountTimeout?: number;
  errorThresholdPercentage?: number;
  defaultErrorThresholdPercentage?: number;
}

export interface ManagedCircuit {
  name: string;
  fire<T>(fn: () => Promise<T>): Promise<T>;
  getState(): CircuitState;
  isOpen(): boolean;
}

function readEnvInt(name: string, fallback: number): number {
  const raw = process.env[name];
  if (!raw) return fallback;
  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

function readEnvPercentage(name: string, fallback: number): number {
  const value = readEnvInt(name, fallback);
  return value <= 100 ? value : fallback;
}

function scopedEnvName(
  prefix: string | undefined,
  suffix: string
): string | null {
  return prefix ? `${prefix}_${suffix}` : null;
}

function readScopedEnvInt(
  prefix: string | undefined,
  suffix: string,
  globalName: string,
  fallback: number
): number {
  const scopedName = scopedEnvName(prefix, suffix);
  if (scopedName && process.env[scopedName]) {
    return readEnvInt(scopedName, fallback);
  }
  return readEnvInt(globalName, fallback);
}

function readScopedEnvPercentage(
  prefix: string | undefined,
  suffix: string,
  globalName: string,
  fallback: number
): number {
  const scopedName = scopedEnvName(prefix, suffix);
  if (scopedName && process.env[scopedName]) {
    return readEnvPercentage(scopedName, fallback);
  }
  return readEnvPercentage(globalName, fallback);
}

function mapBreakerState(breaker: CircuitBreaker): CircuitState {
  if (breaker.halfOpen) return "half-open";
  if (breaker.opened) return "open";
  return "closed";
}

function isBreakerOpenError(error: unknown): boolean {
  return isCircuitOpenLikeError(error);
}

export function createManagedCircuit(
  options: ManagedCircuitOptions
): ManagedCircuit {
  const timeout =
    options.timeout ??
    readScopedEnvInt(
      options.envPrefix,
      "TIMEOUT_MS",
      "CIRCUIT_BREAKER_TIMEOUT_MS",
      options.defaultTimeout ?? 2500
    );
  const resetTimeout =
    options.resetTimeout ??
    readScopedEnvInt(
      options.envPrefix,
      "RESET_MS",
      "CIRCUIT_BREAKER_RESET_MS",
      options.defaultResetTimeout ?? 30_000
    );
  const volumeThreshold =
    options.volumeThreshold ??
    readScopedEnvInt(
      options.envPrefix,
      "FAILURE_THRESHOLD",
      "CIRCUIT_BREAKER_FAILURE_THRESHOLD",
      options.defaultVolumeThreshold ?? 5
    );
  const rollingCountTimeout =
    options.rollingCountTimeout ??
    readScopedEnvInt(
      options.envPrefix,
      "ROLLING_WINDOW_MS",
      "CIRCUIT_BREAKER_ROLLING_WINDOW_MS",
      options.defaultRollingCountTimeout ?? 10_000
    );
  const errorThresholdPercentage =
    options.errorThresholdPercentage ??
    readScopedEnvPercentage(
      options.envPrefix,
      "ERROR_THRESHOLD_PERCENTAGE",
      "CIRCUIT_BREAKER_ERROR_THRESHOLD_PERCENTAGE",
      options.defaultErrorThresholdPercentage ?? 50
    );

  let lastState: CircuitState = "closed";
  let openReason = "threshold_exceeded";

  const breaker = new CircuitBreaker(async <T>(fn: () => Promise<T>) => fn(), {
    timeout,
    resetTimeout,
    rollingCountTimeout,
    volumeThreshold,
    errorThresholdPercentage,
    name: options.name,
  });

  const emitOpenTransition = (reason: string) => {
    const nextState = mapBreakerState(breaker);
    if (nextState === "open" && lastState !== "open") {
      emitCircuitOpenAlert(
        options.name,
        lastState,
        nextState,
        reason,
        breaker.stats.failures
      );
    }
    lastState = nextState;
  };

  breaker.on("timeout", () => {
    openReason = "timeout";
  });
  breaker.on("failure", () => {
    openReason = "failure";
  });
  breaker.on("open", () => {
    emitOpenTransition(openReason);
    openReason = "threshold_exceeded";
  });
  breaker.on("halfOpen", () => {
    lastState = "half-open";
    openReason = "threshold_exceeded";
  });
  breaker.on("close", () => {
    lastState = "closed";
    openReason = "threshold_exceeded";
  });

  return {
    name: options.name,
    getState: () => mapBreakerState(breaker),
    isOpen: () => breaker.opened,
    async fire<T>(fn: () => Promise<T>): Promise<T> {
      try {
        return (await breaker.fire(fn)) as T;
      } catch (error) {
        if (isBreakerOpenError(error) || breaker.opened) {
          throw new CircuitOpenError(options.name);
        }
        throw error;
      }
    },
  };
}
